/** Inventário dos dados de um IFC: que classes têm que Psets, com que
 *  propriedades, de que tipo, em que unidade, e quantos elementos as
 *  preenchem.
 *
 *  Lê o texto do ficheiro e não o que o motor de visualização guardou:
 *  o Fragments só guarda algumas relações por omissão (foi assim que as
 *  zonas e os types desapareceram, até os pedirmos), e um inventário
 *  tem de dizer o que o ficheiro TEM, não o que chegou ao ecrã.
 *
 *  Ideia do liteAECO (IFC Pset Export, Apache-2.0). O código é nosso.
 *
 *  As unidades seguem as regras de sempre (ver unidades.ts): a da
 *  própria propriedade, se a declarar; senão a do projecto para esse
 *  tipo de medida; se o projecto declarar duas, fica ambígua; nunca se
 *  assume SI. Cada unidade diz de que entidade veio. */

import { canonicalIfcName } from '@modules/boq/lib/ifcCanonical'
import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { lerArgTexto, lerListaDeRefs, lerRef, lerValorTipado } from '@viewer/lib/step/textoStep'
import {
  explicarUnidade,
  lerUnidade,
  unidadeDaPropriedade,
  unidadesDoProjecto,
  type Unidade,
} from '@viewer/lib/unidades'
import type { Folha } from '@core/lib/folhas'
import { splitTopLevelArgs } from '@viewer/lib/stepText'
import { valorLegivel } from '@modules/ifc-edit/lib/operacoesEmMassa'
import { classificacoesDe, construirIndiceDeAssociacoes, materialDe } from '@modules/ifc-edit/lib/associacoes'

export interface LinhaDoInventario {
  classe: string
  origem: 'instância' | 'tipo'
  conjunto: string
  genero: 'Pset' | 'Qto'
  propriedade: string
  classeDaPropriedade: string
  tiposDeValor: string[]
  /** Elementos (ou tipos, quando a origem é o tipo) que têm a propriedade. */
  elementos: number
  /** Quantos desses têm valor (não `$`). */
  comValor: number
  valoresDistintos: number
  exemplos: string[]
  unidade: string
  explicacaoDaUnidade: string
  /** Só nos Psets de tipo: quantas instâncias herdam o valor. */
  instanciasAbrangidas?: number
}

export interface MaterialNoInventario {
  /** O nome do IfcMaterial, ou a forma do material composto com o #id. */
  texto: string
  simples: boolean
  elementos: number
}

export interface ClassificacaoNoInventario {
  sistema: string
  codigo: string
  nome: string
  elementos: number
}

export interface Inventario {
  esquema: string | null
  linhas: LinhaDoInventario[]
  materiais: MaterialNoInventario[]
  /** Elementos com material associado (directamente, não pelo tipo). */
  elementosComMaterial: number
  classificacoes: ClassificacaoNoInventario[]
  sistemas: string[]
  unidadesDoProjecto: { tipo: string; simbolo: string; nome: string; origem: string }[]
  unidadesAmbiguas: { tipo: string; candidatas: string[] }[]
  totalDeConjuntos: number
  totalDeElementosComDados: number
}

const MAX_DISTINTOS = 1000

/** Converte uma entidade de unidade do STEP na forma que o lerUnidade
 *  espera (a do web-ifc: `{ type, expressID, UnitType: { value } ... }`). */
function unidadeDoStep(m: ModeloStep, id: number, fundo = 0): unknown {
  const classe = m.classe(id)
  if (!classe || fundo > 5) return null
  const a = m.args(id)
  const en = (x: string | undefined) => ({ value: (x ?? '').replace(/^\.|\.$/g, '') })
  const tx = (x: string | undefined) => ({ value: lerArgTexto(x) ?? (x ?? '').replace(/^\.|\.$/g, '') })
  const base = { type: classe, expressID: id }
  switch (classe) {
    case 'IFCSIUNIT':
      return { ...base, UnitType: en(a[1]), Prefix: a[2] === '$' ? null : en(a[2]), Name: en(a[3]) }
    case 'IFCCONVERSIONBASEDUNIT':
    case 'IFCCONVERSIONBASEDUNITWITHOFFSET': {
      const f = lerRef(a[3])
      const fa = f !== null ? m.args(f) : []
      const v = lerValorTipado(fa[0])
      const comp = lerRef(fa[1])
      return {
        ...base,
        UnitType: en(a[1]),
        Name: tx(a[2]),
        ConversionFactor: {
          ValueComponent: { value: v ? Number(v.interior) : null },
          UnitComponent: comp !== null ? unidadeDoStep(m, comp, fundo + 1) : null,
        },
      }
    }
    case 'IFCDERIVEDUNIT':
      return {
        ...base,
        Elements: lerListaDeRefs(a[0]).map((el) => {
          const ea = m.args(el)
          const u = lerRef(ea[0])
          return { Unit: u !== null ? unidadeDoStep(m, u, fundo + 1) : null, Exponent: { value: Number(ea[1]) } }
        }),
        UnitType: en(a[1]),
        UserDefinedType: tx(a[2]),
      }
    case 'IFCMONETARYUNIT':
      return { ...base, Currency: tx(a[0]) }
    case 'IFCCONTEXTDEPENDENTUNIT':
      return { ...base, UnitType: en(a[1]), Name: tx(a[2]) }
    default:
      return null
  }
}

function lerUnidadesDoProjecto(m: ModeloStep) {
  const projecto = m.idsDaClasse('IFCPROJECT')[0]
  const ua = projecto !== undefined ? lerRef(m.args(projecto)[8]) : null
  const ids = ua !== null ? lerListaDeRefs(m.args(ua)[0]) : []
  return unidadesDoProjecto(ids.map((id) => unidadeDoStep(m, id)))
}

function textoDaUnidade(u: Unidade | null): string {
  if (!u) return ''
  return u.origem ? `${u.simbolo} (#${u.origem.id})` : u.simbolo
}

interface Acumulador {
  classe: string
  origem: LinhaDoInventario['origem']
  conjunto: string
  genero: LinhaDoInventario['genero']
  propriedade: string
  classes: Set<string>
  tipos: Set<string>
  donos: Set<number>
  comValor: Set<number>
  distintos: Set<string>
  exemplos: string[]
  unidades: Map<string, string>
  instancias: Set<number>
}

function valorDaPropriedade(m: ModeloStep, p: number): { valor: string | null; medida: string | null; unidade: number | null } {
  const classe = m.classe(p) ?? ''
  const a = m.args(p)
  if (classe === 'IFCPROPERTYSINGLEVALUE') {
    const v = lerValorTipado(a[2])
    if (!v) return { valor: null, medida: null, unidade: lerRef(a[3]) }
    const t = lerArgTexto(v.interior)
    const valor = t ?? (v.interior === '.T.' ? 'TRUE' : v.interior === '.F.' ? 'FALSE' : v.interior === '.U.' ? 'UNKNOWN' : String(Number(v.interior)))
    return { valor, medida: v.tipo, unidade: lerRef(a[3]) }
  }
  if (classe.startsWith('IFCQUANTITY')) {
    const n = Number(a[3])
    return { valor: Number.isFinite(n) ? String(n) : null, medida: classe, unidade: lerRef(a[2]) }
  }
  if (classe === 'IFCPROPERTYLISTVALUE') {
    const t = (a[2] ?? '').trim()
    const itens = t.startsWith('(') && t.length > 2 ? splitTopLevelArgs(t.slice(1, -1)).map((x) => x.trim()) : []
    const valores = itens.map(valorLegivel).filter((x): x is string => x !== null)
    return { valor: valores.length ? valores.join(', ') : null, medida: lerValorTipado(itens[0])?.tipo ?? null, unidade: lerRef(a[3]) }
  }
  if (classe === 'IFCPROPERTYBOUNDEDVALUE') {
    // UpperBoundValue (2), LowerBoundValue (3), Unit (4).
    const max = valorLegivel(a[2]) ?? ''
    const min = valorLegivel(a[3]) ?? ''
    const medida = lerValorTipado(a[2])?.tipo ?? lerValorTipado(a[3])?.tipo ?? null
    return { valor: min || max ? `${min}..${max}` : null, medida, unidade: lerRef(a[4]) }
  }
  if (classe === 'IFCPROPERTYENUMERATEDVALUE') {
    const lista = (a[2] ?? '').trim()
    if (lista === '$') return { valor: null, medida: null, unidade: null }
    const valores = [...lista.matchAll(/'((?:[^']|'')*)'/g)].map((x) => lerArgTexto(`'${x[1]}'`) ?? '')
    return { valor: valores.join(', ') || null, medida: null, unidade: null }
  }
  return { valor: null, medida: null, unidade: null }
}

/** A unidade de cada propriedade ou quantidade do ficheiro, em texto
 *  ("mm (#11)"), pelas mesmas regras do inventário. Null quando o valor
 *  não leva unidade (texto, booleano, razão, contagem). */
export function criarLeitorDeUnidades(m: ModeloStep): (propriedade: number) => string | null {
  const { porTipo, ambiguas } = lerUnidadesDoProjecto(m)
  const cache = new Map<number, Unidade | null>()
  return (p) => {
    const v = valorDaPropriedade(m, p)
    if (!v.medida) return null
    let propria: Unidade | null = null
    if (v.unidade !== null) {
      if (!cache.has(v.unidade)) cache.set(v.unidade, lerUnidade(unidadeDoStep(m, v.unidade)))
      propria = cache.get(v.unidade) ?? null
    }
    const u = unidadeDaPropriedade(v.medida, propria, porTipo, ambiguas)
    if (u.estado === 'propria' || u.estado === 'projecto') return textoDaUnidade(u.unidade)
    if (u.estado === 'ambigua') return 'unidade ambígua'
    if (u.estado === 'em-falta') return 'unidade não declarada'
    return null
  }
}

export function inventariar(m: ModeloStep): Inventario {
  const { porTipo, ambiguas } = lerUnidadesDoProjecto(m)
  const unidadePropriaCache = new Map<number, Unidade | null>()
  const unidadePropria = (id: number | null): Unidade | null => {
    if (id === null) return null
    if (!unidadePropriaCache.has(id)) unidadePropriaCache.set(id, lerUnidade(unidadeDoStep(m, id)))
    return unidadePropriaCache.get(id) ?? null
  }

  // Instâncias por tipo, para os Psets de tipo.
  const instanciasDoTipo = new Map<number, number[]>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYTYPE')) {
    const a = m.args(rel)
    const t = lerRef(a[5])
    if (t !== null) instanciasDoTipo.set(t, [...(instanciasDoTipo.get(t) ?? []), ...lerListaDeRefs(a[4])])
  }

  const acc = new Map<string, Acumulador>()
  let totalDeConjuntos = 0
  const comDados = new Set<number>()

  const registar = (dono: number, origem: LinhaDoInventario['origem'], conjunto: number) => {
    const cls = m.classe(conjunto)
    if (cls !== 'IFCPROPERTYSET' && cls !== 'IFCELEMENTQUANTITY') return
    const nomeConjunto = lerArgTexto(m.args(conjunto)[2]) ?? '(sem nome)'
    const genero = cls === 'IFCELEMENTQUANTITY' ? 'Qto' : 'Pset'
    const lista = lerListaDeRefs(m.args(conjunto)[cls === 'IFCELEMENTQUANTITY' ? 5 : 4])
    const classeDono = canonicalIfcName(m.classe(dono) ?? '')
    for (const p of lista) {
      const nome = lerArgTexto(m.args(p)[0]) ?? '(sem nome)'
      const chave = `${classeDono}\0${origem}\0${nomeConjunto}\0${nome}`
      let a = acc.get(chave)
      if (!a) {
        a = {
          classe: classeDono,
          origem,
          conjunto: nomeConjunto,
          genero,
          propriedade: nome,
          classes: new Set(),
          tipos: new Set(),
          donos: new Set(),
          comValor: new Set(),
          distintos: new Set(),
          exemplos: [],
          unidades: new Map(),
          instancias: new Set(),
        }
        acc.set(chave, a)
      }
      a.classes.add(canonicalIfcName(m.classe(p) ?? ''))
      a.donos.add(dono)
      if (origem === 'tipo') for (const i of instanciasDoTipo.get(dono) ?? []) a.instancias.add(i)
      const v = valorDaPropriedade(m, p)
      if (v.medida && !v.medida.startsWith('IFCQUANTITY')) a.tipos.add(canonicalIfcName(v.medida))
      if (v.valor !== null) {
        a.comValor.add(dono)
        if (a.distintos.size < MAX_DISTINTOS) a.distintos.add(v.valor)
        if (a.exemplos.length < 3 && !a.exemplos.includes(v.valor)) a.exemplos.push(v.valor)
      }
      if (v.medida) {
        const u = unidadeDaPropriedade(v.medida, unidadePropria(v.unidade), porTipo, ambiguas)
        const texto =
          u.estado === 'propria' || u.estado === 'projecto'
            ? textoDaUnidade(u.unidade)
            : u.estado === 'ambigua'
              ? 'ambígua'
              : u.estado === 'em-falta'
                ? 'em falta'
                : u.estado === 'adimensional'
                  ? 'sem dimensão'
                  : ''
        const explicacao = (u.estado === 'propria' ? 'Na propriedade. ' : u.estado === 'projecto' ? 'Do projecto. ' : '') + explicarUnidade(u)
        if (!a.unidades.has(texto)) a.unidades.set(texto, explicacao)
      }
    }
  }

  for (const rel of m.idsDaClasse('IFCRELDEFINESBYPROPERTIES')) {
    const a = m.args(rel)
    const objs = lerListaDeRefs(a[4])
    const r = lerRef(a[5])
    const conjuntos = r !== null ? [r] : lerListaDeRefs(a[5])
    for (const c of conjuntos) {
      totalDeConjuntos++
      for (const o of objs) {
        comDados.add(o)
        registar(o, 'instância', c)
      }
    }
  }
  for (const id of m.todos()) {
    const classe = m.classe(id)!
    if (!classe.endsWith('TYPE') && !classe.endsWith('STYLE')) continue
    const a = m.args(id)
    if (a.length < 6 || !/^'.{22}'$/.test(a[0] ?? '')) continue
    for (const c of lerListaDeRefs(a[5])) {
      totalDeConjuntos++
      registar(id, 'tipo', c)
    }
  }

  const linhas: LinhaDoInventario[] = [...acc.values()]
    .map((a) => {
      const unidades = [...a.unidades.entries()].filter(([t]) => t !== '')
      return {
        classe: a.classe,
        origem: a.origem,
        conjunto: a.conjunto,
        genero: a.genero,
        propriedade: a.propriedade,
        classeDaPropriedade: [...a.classes].join(', '),
        tiposDeValor: [...a.tipos].sort(),
        elementos: a.donos.size,
        comValor: a.comValor.size,
        valoresDistintos: a.distintos.size,
        exemplos: a.exemplos,
        unidade: unidades.length > 1 ? `várias: ${unidades.map(([t]) => t).join('; ')}` : (unidades[0]?.[0] ?? ''),
        explicacaoDaUnidade: unidades.map(([, e]) => e).join(' | '),
        instanciasAbrangidas: a.origem === 'tipo' ? a.instancias.size : undefined,
      }
    })
    .sort(
      (x, y) =>
        x.classe.localeCompare(y.classe) ||
        x.origem.localeCompare(y.origem) ||
        x.conjunto.localeCompare(y.conjunto) ||
        x.propriedade.localeCompare(y.propriedade),
    )

  // Materiais e classificações, por elemento (como o modelo de
  // preenchimento os mostra).
  const ix = construirIndiceDeAssociacoes(m)
  const materiais = new Map<string, MaterialNoInventario>()
  for (const obj of ix.materiais.keys()) {
    const x = materialDe(m, ix, obj)
    if (x.texto === null) continue
    const k = `${x.simples ? 's' : 'c'}\0${x.texto}`
    const a = materiais.get(k)
    if (a) a.elementos++
    else materiais.set(k, { texto: x.texto, simples: x.simples, elementos: 1 })
  }
  const classificacoes = new Map<string, ClassificacaoNoInventario>()
  for (const obj of ix.classificacoes.keys()) {
    for (const c of classificacoesDe(m, ix, obj)) {
      const k = `${c.sistema}\0${c.codigo ?? ''}`
      const a = classificacoes.get(k)
      if (a) a.elementos++
      else classificacoes.set(k, { sistema: c.sistema, codigo: c.codigo ?? '', nome: c.nome ?? '', elementos: 1 })
    }
  }
  const listaDeClassificacoes = [...classificacoes.values()].sort(
    (x, y) => x.sistema.localeCompare(y.sistema) || x.codigo.localeCompare(y.codigo, undefined, { numeric: true }),
  )

  return {
    esquema: m.esquema(),
    linhas,
    materiais: [...materiais.values()].sort((x, y) => y.elementos - x.elementos || x.texto.localeCompare(y.texto)),
    elementosComMaterial: [...ix.materiais.keys()].filter((o) => materialDe(m, ix, o).texto !== null).length,
    classificacoes: listaDeClassificacoes,
    sistemas: [...new Set(listaDeClassificacoes.map((c) => c.sistema))],
    unidadesDoProjecto: [...porTipo.values()].map((u) => ({
      tipo: u.tipo,
      simbolo: u.simbolo,
      nome: u.nome,
      origem: u.origem ? `#${u.origem.id}` : '',
    })),
    unidadesAmbiguas: [...ambiguas.entries()].map(([tipo, us]) => ({ tipo, candidatas: us.map(textoDaUnidade) })),
    totalDeConjuntos,
    totalDeElementosComDados: comDados.size,
  }
}

/** O inventário numa folha de Excel, com uma folha a explicar as
 *  colunas e as unidades do projecto. */
export function inventarioParaFolhas(inv: Inventario, ficheiro: string, quando: string): Folha[] {
  const MAX_DISTINTOS_TXT = (n: number) => (n >= MAX_DISTINTOS ? `${MAX_DISTINTOS}+` : n)
  return [
    {
      nome: 'Inventário',
      linhas: [
        [
          'Classe', 'Origem', 'Conjunto', 'Pset/Qto', 'Propriedade', 'Classe da propriedade',
          'Tipo de valor', 'Unidade', 'Elementos', 'Com valor', 'Sem valor', 'Valores distintos',
          'Exemplos', 'Instâncias do tipo', 'De onde vem a unidade',
        ],
        ...inv.linhas.map((l) => [
          l.classe, l.origem, l.conjunto, l.genero, l.propriedade, l.classeDaPropriedade,
          l.tiposDeValor.join(', '), l.unidade, l.elementos, l.comValor, l.elementos - l.comValor,
          MAX_DISTINTOS_TXT(l.valoresDistintos), l.exemplos.join(' | '), l.instanciasAbrangidas ?? null,
          l.explicacaoDaUnidade,
        ]),
      ],
    },
    {
      nome: 'Unidades do projecto',
      linhas: [
        ['Tipo', 'Símbolo', 'Nome', 'Entidade no ficheiro'],
        ...inv.unidadesDoProjecto.map((u) => [u.tipo, u.simbolo, u.nome, u.origem]),
        ...inv.unidadesAmbiguas.map((u) => [u.tipo, 'ambígua', u.candidatas.join('; '), 'o IfcUnitAssignment declara mais de uma']),
      ],
    },
    {
      nome: 'Materiais',
      linhas: [
        ['Material', 'Simples', 'Elementos'],
        ...inv.materiais.map((x) => [x.texto, x.simples ? 'sim' : 'não (composto)', x.elementos]),
      ],
    },
    {
      nome: 'Classificações',
      linhas: [
        ['Sistema', 'Código', 'Nome', 'Elementos'],
        ...inv.classificacoes.map((c) => [c.sistema, c.codigo, c.nome, c.elementos]),
      ],
    },
    {
      nome: 'Leia-me',
      linhas: [
        ['Inventário de propriedades'],
        [`Ficheiro: ${ficheiro}`],
        [`Esquema: ${inv.esquema ?? 'não declarado'}`],
        [`Gerado em ${quando} pelo VN2R openBIM Hub, a partir do texto do IFC.`],
        [''],
        ['Origem: "instância" é um Pset ligado ao elemento; "tipo" é um Pset do tipo (IfcTypeObject), herdado pelas instâncias.'],
        ['Unidade: a da própria propriedade quando a declara; senão a do projecto para esse tipo de medida. Nunca se assume SI.'],
        ['"ambígua": o projecto declara duas unidades para o mesmo tipo e não se escolhe nenhuma. "em falta": não há unidade declarada.'],
        ['O número entre parênteses (#12) é a entidade do ficheiro de onde veio a unidade.'],
        ['Valores distintos contam-se até 1000.'],
        ['Listas: os valores separados por vírgulas. Intervalos: min..max.'],
        ['Materiais: o material associado a cada elemento (IfcRelAssociatesMaterial); um composto (camadas, perfis, constituintes) aparece com a forma e o #id.'],
        ['Classificações: a referência de cada elemento em cada sistema (IfcRelAssociatesClassification).'],
      ],
    },
  ]
}
